import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useCallback, useMemo } from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import { useFormikContext } from 'formik'
import { useNavigate } from 'react-router-dom'

import useMediaQuery from '@mui/material/useMediaQuery'

import { IAddOpportunityFormModel } from '../../typings'
import { SubHeader } from '../FormComponents/SubHeader/SubHeader'
import { ShowRequiredFieldsToggle } from '../FormComponents/ShowRequiredFieldsToggle/ShowRequiredFieldsToggle'
import { useNonRequiredFieldValidate } from './hooks/useNonRequiredFieldValidate'
import { NoteCode } from '@user/components/Notes/typings'

type Props = {
  onSave: () => void
  hideOptional: boolean
  onChangeHideOptional: (
    e: React.ChangeEvent<HTMLInputElement> | null,
    checked: boolean,
  ) => void
  disableSaveAndView: boolean
  isClosedWon: boolean | undefined
  isClosedLost: boolean | undefined
  noteCodes: NoteCode[]
}

export function AddOpportunityFormHeader(props: Props) {
  const {
    disableSaveAndView,
    onSave,
    isClosedWon,
    isClosedLost,
    hideOptional,
    onChangeHideOptional,
    noteCodes,
  } = props
  const navigate = useNavigate()

  const onCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])
  const isMobile = useMediaQuery('(max-width:900px)')

  const { errors, values, isSubmitting, setFieldValue } =
    useFormikContext<IAddOpportunityFormModel>()

  const { checkNonRequiredErrors } = useNonRequiredFieldValidate({
    setHideOptional: (value) => onChangeHideOptional(null, value),
    errors,
    values,
  })

  const saveForm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setFieldValue('viewDetail', event.currentTarget.innerText !== 'Save')

      const nonRequiredHasErrors = checkNonRequiredErrors()
      if (!nonRequiredHasErrors) onSave()
    },
    [setFieldValue, onSave, checkNonRequiredErrors],
  )

  const hideSaveAndView = isClosedWon || isClosedLost || disableSaveAndView

  const buttonText = useMemo(() => {
    if (isClosedWon) {
      return 'Close Win'
    }
    if (isClosedLost) {
      return 'Close Lost...'
    }
    return 'Save'
  }, [isClosedWon, isClosedLost])

  const theme = useTheme()

  return (
    <Box>
      <Paper
        square
        elevation={0}
        sx={{
          padding: theme.spacing(1),
          flexWrap: 'nowrap',
          backgroundColor: isMobile
            ? theme.palette.grey[50]
            : theme.palette.common.white,
          borderBottom: '1px solid #e0e0e0',
          paddingLeft: theme.spacing(2),
        }}
      >
        <Box display="flex" ml={0} mr={0.8}>
          <ShowRequiredFieldsToggle {...props} />

          <Button
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
            variant="text"
            data-test-id="cancelOpportunityBtn"
            sx={{ marginLeft: theme.spacing(1), textTransform: 'none' }}
          >
            Cancel
          </Button>
          {!hideSaveAndView && (
            <Button
              variant="contained"
              onClick={saveForm}
              disabled={disableSaveAndView || isSubmitting}
              data-test-id="saveAndViewOpportunityBtn"
              sx={{ marginLeft: theme.spacing(1), textTransform: 'none' }}
            >
              Save and View
            </Button>
          )}
          <Button
            disabled={isSubmitting}
            onClick={saveForm}
            variant="contained"
            color="primary"
            data-test-id="saveOpportunityBtn"
            sx={{ marginLeft: theme.spacing(1), textTransform: 'none' }}
          >
            {buttonText}
          </Button>
        </Box>
      </Paper>
      {isMobile && (
        <SubHeader hideOptional={hideOptional} noteCodes={noteCodes} />
      )}
    </Box>
  )
}
